import debounce from 'lodash/debounce';
import type { MutableRefObject } from 'react';

import { isInfiniteScrollDown } from 'logview/helpers';
import type { InfiniteScrollDirection } from 'logview/types';

import { LOADING_ROW_HEIGHT } from './LogViewTable';
import type { TableRef } from './LogViewWidget';

const ON_BORDER_REACH_DEBOUNCE_WAIT = 500;
const ON_BORDER_REACH_DEBOUNCE_OPTIONS = { leading: true, trailing: false };

const _thresholds = (
  tableScrollHeight: number,
  tableClientHeight: number,
  infiniteScrollDirection: InfiniteScrollDirection,
) => {
  const bottomThreshold = Math.round((tableScrollHeight - tableClientHeight) / 5);
  const topThreshold = LOADING_ROW_HEIGHT;

  if (isInfiniteScrollDown(infiniteScrollDirection)) {
    return {
      bottomThreshold: topThreshold,
      topThreshold: bottomThreshold,
    };
  }

  return {
    bottomThreshold,
    topThreshold,
  };
};

const debouncedOnBottomReach = debounce(
  (onBottomReach: () => void) => {
    onBottomReach();
  },
  ON_BORDER_REACH_DEBOUNCE_WAIT,
  ON_BORDER_REACH_DEBOUNCE_OPTIONS,
);

const debouncedOnTopReach = debounce(
  (onTopReach: () => void) => {
    onTopReach();
  },
  ON_BORDER_REACH_DEBOUNCE_WAIT,
  ON_BORDER_REACH_DEBOUNCE_OPTIONS,
);

const _onTopOrBottomReach = debounce(
  ({
    bottomThreshold,
    onBottomReach,
    onTopReach,
    tableClientHeight,
    tableScrollHeight,
    tableScrollTop,
    topThreshold,
  }: {
    bottomThreshold: number;
    onBottomReach: () => void;
    onTopReach: () => void;
    tableClientHeight: number;
    tableScrollHeight: number;
    tableScrollTop: number;
    topThreshold: number;
  }) => {
    if (tableScrollTop <= topThreshold) {
      debouncedOnTopReach(onTopReach);
    }

    if (tableScrollHeight - tableScrollTop - tableClientHeight <= bottomThreshold) {
      debouncedOnBottomReach(onBottomReach);
    }
  },
  100,
);

const onScroll = ({
  onBottomReach,
  onTopReach,
  tableRef,
  infiniteScrollDirection,
  finishedScrollPositionUpdateRef,
  loadPrevPromiseRef,
}: {
  onBottomReach: () => void;
  onTopReach: () => void;
  tableRef: TableRef;
  infiniteScrollDirection: InfiniteScrollDirection;
  finishedScrollPositionUpdateRef: MutableRefObject<boolean>;
  loadPrevPromiseRef: MutableRefObject<Promise<void>>;
}) => {
  const tableScrollTop = tableRef.current.scrollTop;
  const tableScrollHeight = tableRef.current.scrollHeight;
  const tableClientHeight = tableRef.current.clientHeight;
  const { bottomThreshold, topThreshold } = _thresholds(tableScrollHeight, tableClientHeight, infiniteScrollDirection);

  if (finishedScrollPositionUpdateRef.current && !loadPrevPromiseRef?.current) {
    _onTopOrBottomReach({
      bottomThreshold,
      onBottomReach,
      onTopReach,
      tableClientHeight,
      tableScrollHeight,
      tableScrollTop,
      topThreshold,
    });
  }
};

export default onScroll;
